import { escapeHtmlAttr, escapeJsonForScript } from "./escape";
import type { PageMeta, MetaContext, Locale } from "./types";
import {
  alternateLocale,
  defaultLocaleUrl,
  localeHreflang,
  localeUrl,
} from "./seo-url";
import { absoluteUrl as absUrl } from "./absolute-url";

const SITE_NAME = "Psihotron";
const DEFAULT_OG_IMAGE = "/og-default.jpg";
const INDEXABLE_ROBOTS =
  "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1";

function ogLocale(l: Locale): string {
  return l === "bs" ? "bs_BA" : "en_US";
}

export interface MetaInputs {
  title: string;
  description: string;
  ogImage: string;
  ogType: "website" | "article" | "profile";
  canonical: string;
  locale: Locale;
  altUrl: string;
  xDefaultUrl?: string;
  alternates?: boolean;
  noIndex?: boolean;
  jsonLd?: Record<string, unknown>;
}

/**
 * Pure helper: builds the `<head>` meta string from already-resolved inputs.
 */
export function buildMetaTags(inputs: MetaInputs): string {
  const includeAlternates = inputs.alternates !== false;
  const lines: (string | false | undefined)[] = [
    `<title>${escapeHtmlAttr(inputs.title)}</title>`,
    `<meta name="description" content="${escapeHtmlAttr(inputs.description)}">`,
    `<link rel="canonical" href="${escapeHtmlAttr(inputs.canonical)}">`,
    `<meta name="robots" content="${inputs.noIndex ? "noindex,nofollow" : INDEXABLE_ROBOTS}">`,
    `<meta property="og:type" content="${inputs.ogType}">`,
    `<meta property="og:title" content="${escapeHtmlAttr(inputs.title)}">`,
    `<meta property="og:description" content="${escapeHtmlAttr(inputs.description)}">`,
    `<meta property="og:image" content="${escapeHtmlAttr(inputs.ogImage)}">`,
    `<meta property="og:url" content="${escapeHtmlAttr(inputs.canonical)}">`,
    `<meta property="og:site_name" content="${SITE_NAME}">`,
    `<meta property="og:locale" content="${ogLocale(inputs.locale)}">`,
    includeAlternates && `<meta property="og:locale:alternate" content="${ogLocale(alternateLocale(inputs.locale))}">`,
    `<meta name="twitter:card" content="summary_large_image">`,
    `<meta name="twitter:title" content="${escapeHtmlAttr(inputs.title)}">`,
    `<meta name="twitter:description" content="${escapeHtmlAttr(inputs.description)}">`,
    `<meta name="twitter:image" content="${escapeHtmlAttr(inputs.ogImage)}">`,
    includeAlternates && `<link rel="alternate" hreflang="${localeHreflang(inputs.locale)}" href="${escapeHtmlAttr(inputs.canonical)}">`,
    includeAlternates && `<link rel="alternate" hreflang="${localeHreflang(alternateLocale(inputs.locale))}" href="${escapeHtmlAttr(inputs.altUrl)}">`,
    includeAlternates && `<link rel="alternate" hreflang="x-default" href="${escapeHtmlAttr(inputs.xDefaultUrl ?? inputs.canonical)}">`,
    inputs.jsonLd && `<script type="application/ld+json">${escapeJsonForScript(inputs.jsonLd)}</script>`,
  ];
  return lines.filter(Boolean).join("\n    ");
}

/**
 * Returns the head HTML string injected into the SSR'd document.
 */
export function buildHeadString(meta: PageMeta, ctx: MetaContext): string {
  const title = ctx.t(meta.titleKey);
  const description = ctx.t(meta.descriptionKey);
  const origin = ctx.url.origin;
  const canonical = localeUrl(origin, ctx.url.pathname, ctx.locale);
  const ogImage = absUrl(meta.ogImage ?? DEFAULT_OG_IMAGE, origin);
  const ogType = meta.ogType ?? "website";
  const altUrl = localeUrl(origin, ctx.url.pathname, alternateLocale(ctx.locale));
  const xDefaultUrl = defaultLocaleUrl(origin, ctx.url.pathname);
  return buildMetaTags({
    title,
    description,
    ogImage,
    ogType,
    canonical,
    locale: ctx.locale,
    altUrl,
    xDefaultUrl,
    noIndex: meta.noIndex,
    jsonLd: meta.structuredData?.(ctx),
  });
}

/**
 * Returns the shape TanStack Router's `head` API expects, fed from the same
 * PageMeta. Used for client-side route navigation updates.
 */
export function buildHeadConfig(meta: PageMeta, ctx: MetaContext) {
  const title = ctx.t(meta.titleKey);
  const description = ctx.t(meta.descriptionKey);
  const origin = ctx.url.origin;
  const canonical = localeUrl(origin, ctx.url.pathname, ctx.locale);
  const ogImage = absUrl(meta.ogImage ?? DEFAULT_OG_IMAGE, origin);
  const ogType = meta.ogType ?? "website";
  const altUrl = localeUrl(origin, ctx.url.pathname, alternateLocale(ctx.locale));
  const xDefaultUrl = defaultLocaleUrl(origin, ctx.url.pathname);

  return {
    meta: [
      { title },
      { name: "description", content: description },
      { name: "robots", content: meta.noIndex ? "noindex,nofollow" : INDEXABLE_ROBOTS },
      { property: "og:type", content: ogType },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:image", content: ogImage },
      { property: "og:url", content: canonical },
      { property: "og:site_name", content: SITE_NAME },
      { property: "og:locale", content: ogLocale(ctx.locale) },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: ogImage },
    ].filter(Boolean),
    links: [
      { rel: "canonical", href: canonical },
      { rel: "alternate", hreflang: localeHreflang(ctx.locale), href: canonical },
      { rel: "alternate", hreflang: localeHreflang(alternateLocale(ctx.locale)), href: altUrl },
      { rel: "alternate", hreflang: "x-default", href: xDefaultUrl },
    ],
  };
}
